import React, { useState, useMemo, useEffect } from 'react';
import { Home, Filter, Check, Star, Zap, DollarSign, X, Loader2, Bed, Bath, Building, Map as MapIcon, EyeOff, MapPin } from 'lucide-react';
import { Property } from "../types";
import PropertyCard from "./PropertyCard";
import SearchBar from "./SearchBar";
import {createSearchParams, useNavigate} from 'react-router-dom';

// --- IMPORT LEAFLET ---
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix per le icone di default di Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
    iconSize: [25, 41],
    iconAnchor: [12, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// --- UTILS PER GEOLOCALIZZAZIONE ---

// Fallback statico
const CITY_COORDS_FALLBACK: Record<string, [number, number]> = {
    'new york': [40.7128, -74.0060],
    'hamptons': [40.8980, -72.3895],
    'miami': [25.7617, -80.1918],
};

// Formula di Haversine per calcolare distanza in KM
const getDistanceFromLatLonInKm = (lat1: number, lon1: number, lat2: number, lon2: number) => {
    const R = 6371; // Raggio della terra in km
    const dLat = deg2rad(lat2 - lat1);
    const dLon = deg2rad(lon2 - lon1);
    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return Math.round(R * c);
};

const convertInMiles = (value: number) => {
    const conversionFactor = 0.621371;
    return Math.trunc(value * conversionFactor);
}
const deg2rad = (deg: number) => {
    return deg * (Math.PI / 180);
};

// Marker Prezzo Custom
const createPriceIcon = (label: string, isSecondary: boolean = false) => {
    const bgColor = isSecondary ? '#E84C5B' : 'white';
    const textColor = isSecondary ? 'white' : '#1F2A33';

    return L.divIcon({
        className: 'custom-price-marker',
        html: `<div style="
            background-color: ${bgColor};
            color: ${textColor};
            font-weight: 800;
            padding: 3px 5px;
            border-radius: 8px;
            box-shadow: 0 4px 10px rgba(0,0,0,0.2);
            border: 1px solid rgba(0,0,0,0.05);
            font-size: 12px;
            font-family: sans-serif;
            text-align: center;
            white-space: nowrap;
            display: flex;
            align-items: center;
            justify-content: center;
            min-width: 30px;
            transform: translate(-50%, -50%);
        ">${label}</div>`,
        iconSize: [50, 30],
        iconAnchor: [25, 15]
    });
};

// Componente per aggiornare la vista della mappa
const MapUpdater = ({ center }: { center: [number, number] }) => {
    const map = useMap();
    map.setView(center, 12);
    return null;
};

interface VillasContentProps {
    properties: Property[];
    activeCategory: string;
    setActiveCategory: (category: string) => void;
    searchParams: { location: string; checkIn: string; checkOut: string; guests: number };
    setSearchParams: React.Dispatch<React.SetStateAction<{ location: string; checkIn: string; checkOut: string; guests: number }>>;
    onSearch: () => void;
    loading?: boolean;
}

const VillasContent = ({
                           properties,
                           activeCategory,
                           setActiveCategory,
                           searchParams,
                           setSearchParams,
                           onSearch,
                           loading
                       }: VillasContentProps) => {

    const navigate = useNavigate();

    // --- LOCAL STATES ---
    const [selectedCity, setSelectedCity] = useState<string>('All');
    const [selectedType, setSelectedType] = useState<string>('All');
    const [minBedrooms, setMinBedrooms] = useState<number>(0);
    const [minBaths, setMinBaths] = useState<number>(0);
    const [priceRange, setPriceRange] = useState<[number, number]>([0, 50000]);
    const [selectedAmenities, setSelectedAmenities] = useState<string[]>([]);
    const [showMobileFilters, setShowMobileFilters] = useState(false);
    const [showMap, setShowMap] = useState(true);

    // NUOVO STATO: Traccia se la ricerca è stata confermata col pulsante
    const [isSearchApplied, setIsSearchApplied] = useState(false);

    // RESETTA la visualizzazione "Totale" se l'utente cambia le date ma non ha ancora cliccato cerca
    useEffect(() => {
        setIsSearchApplied(false);
    }, [searchParams.checkIn, searchParams.checkOut]);

    // WRAPPER PER LA RICERCA
    const handleSearchClick = () => {
        setIsSearchApplied(true);
        onSearch(); // Chiama la funzione originale passata dal genitore
    };

    const cities = ['All', ...Array.from(new Set(properties.map(p => p.city || p.location.split(',')[0]).filter(Boolean)))];
    const propertyTypes = ['All', ...Array.from(new Set(properties.map(p => p.type).filter(Boolean)))];

    const amenitiesMap = [
        { label: "Pool", key: "Private pool" },
        { label: "Wifi", key: "Wireless Internet" },
        { label: "BBQ Area", key: "BBQ grill" },
        { label: "Tennis Court", key: "Tennis" },
        { label: "Pets Allowed", key: "Pets allowed" },
        { label: "Fireplace", key: "Indoor fireplace" },
    ];

    const categories = [
        { id: 'All', label: 'All', icon: Home },
        { id: 'Owner Approval', label: 'Owner Approval', icon: Star },
        { id: 'Direct Booking', label: 'Direct Booking', icon: Zap },
    ];

    const toggleAmenity = (amenityKey: string) => {
        setSelectedAmenities(prev =>
            prev.includes(amenityKey) ? prev.filter(a => a !== amenityKey) : [...prev, amenityKey]
        );
    };

    const navigateToDetails = (propertyId: string) => {
        const queryParams: any = {};

        if (isSearchApplied) {
            if (searchParams.checkIn) queryParams.checkIn = searchParams.checkIn;
            if (searchParams.checkOut) queryParams.checkOut = searchParams.checkOut;
            if (searchParams.guests) queryParams.guests = searchParams.guests.toString();
        }

        navigate({
            pathname: `/details/${propertyId}`,
            search: createSearchParams(queryParams).toString()
        });
    };

    // --- LOGICA DI FILTRAGGIO ---
    const matchesLocalFilters = (p: Property) => {
        if (activeCategory === 'Owner Approval' && p.tag !== 'Owner Approval') return false;
        if (activeCategory === 'Direct Booking' && p.tag !== 'Direct Booking') return false;

        if (selectedCity !== 'All' && !p.location.includes(selectedCity) && !p.city?.includes(selectedCity)) return false;

        if (selectedType !== 'All' && p.type !== selectedType) return false;
        if ((p.bedrooms || 0) < minBedrooms) return false;
        if ((p.baths || 0) < minBaths) return false;
        const price = p.price || 0;
        if (price < priceRange[0] || price > priceRange[1]) return false;

        if (selectedAmenities.length > 0) {
            const pAmenities = p.amenities || [];
            const hasAll = selectedAmenities.every(reqAmenity => {
                if (reqAmenity === 'Private pool') return pAmenities.some(a => a.toLowerCase().includes('pool'));
                if (reqAmenity === 'Tennis') return pAmenities.some(a => a.toLowerCase().includes('tennis'));
                if (reqAmenity === 'BBQ grill') return pAmenities.some(a => a.toLowerCase().includes('bbq') || a.toLowerCase().includes('barbecue'));
                return pAmenities.includes(reqAmenity);
            });
            if (!hasAll) return false;
        }
        return true;
    };

    // 1. Risultati Esatti
    const exactMatches = useMemo(() => {
        return properties.filter(p => {
            let isLocationMatch = true;
            if (searchParams.location && searchParams.location !== 'All') {
                const search = searchParams.location.toLowerCase();
                const pLoc = (p.location + ' ' + p.city).toLowerCase();
                isLocationMatch = pLoc.includes(search);
            }
            return isLocationMatch && matchesLocalFilters(p);
        });
    }, [properties, selectedCity, selectedType, minBedrooms, minBaths, priceRange, selectedAmenities, activeCategory, searchParams.location]);

    // 2. Risultati Suggeriti
    const suggestedMatches = useMemo(() => {
        const searchCityKey = searchParams.location ? searchParams.location.toLowerCase() : '';
        let searchCoords: [number, number] | null = null;

        // MODIFICA: Esegui la ricerca dell'anchorProperty SOLO se c'è una chiave di ricerca valida
        if (searchCityKey.trim() !== '') {
            const anchorProperty = properties.find(p => {
                const pCity = p.city ? p.city.toLowerCase() : '';
                const pLoc = p.location ? p.location.toLowerCase() : '';
                return (pCity.includes(searchCityKey) || pLoc.includes(searchCityKey)) && p.lat && p.lng;
            });

            if (anchorProperty && anchorProperty.lat && anchorProperty.lng) {
                searchCoords = [anchorProperty.lat, anchorProperty.lng];
            } else {
                const fallbackKey = Object.keys(CITY_COORDS_FALLBACK).find(k => searchCityKey.includes(k));
                if (fallbackKey) {
                    searchCoords = CITY_COORDS_FALLBACK[fallbackKey];
                }
            }
        }

        const others = properties
            .filter(p => !exactMatches.includes(p))
            .filter(p => matchesLocalFilters(p))
            .map(p => {
                let dist = null;
                if (searchCoords && p.lat && p.lng) {
                    dist = convertInMiles(getDistanceFromLatLonInKm(searchCoords[0], searchCoords[1], p.lat, p.lng));
                }

                const serverDist = (p as any).distanceFromSearch;
                const finalDist = serverDist ? convertInMiles(serverDist) : dist;

                return { ...p, distanceFromSearch: finalDist };
            });

        return others.sort((a, b) => (a.distanceFromSearch || 99999) - (b.distanceFromSearch || 99999));

    }, [properties, exactMatches, searchParams.location, selectedCity, selectedType, minBedrooms, minBaths, priceRange, selectedAmenities, activeCategory]);

    const activeFiltersCount = selectedAmenities.length + (minBedrooms > 0 ? 1 : 0) + (minBaths > 0 ? 1 : 0) + (selectedType !== 'All' ? 1 : 0);

    const mapCenter: [number, number] =
        exactMatches.length > 0 && exactMatches[0].lat && exactMatches[0].lng
            ? [exactMatches[0].lat, exactMatches[0].lng]
            : suggestedMatches.length > 0 && suggestedMatches[0].lat && suggestedMatches[0].lng
                ? [suggestedMatches[0].lat, suggestedMatches[0].lng]
                : [40.90, -72.35];

    return (
        <div className="pt-24 md:pt-32 px-4 md:px-8 max-w-[1800px] mx-auto animate-fade-in min-h-screen pb-20 relative">

            {/* SEARCH BAR */}
            <div className="mb-6 md:mb-8 relative z-40">
                <SearchBar
                    params={searchParams}
                    setParams={setSearchParams}
                    onSearch={handleSearchClick}
                />
            </div>

            {/* FILTER HEADER */}
            <div className="flex flex-col gap-4 md:gap-6 mb-8 sticky top-20 bg-white z-30 py-2 md:py-4 border-b border-gray-100 shadow-sm md:shadow-none -mx-4 px-4 md:mx-0 md:px-0">
                {/* Categories */}
                <div className="flex items-center gap-6 md:gap-8 border-b border-gray-100 pb-1 overflow-x-auto no-scrollbar -mx-4 px-4 md:mx-0 md:px-0">
                    {categories.map(cat => (
                        <button key={cat.id} onClick={() => setActiveCategory(cat.id)} className={`flex-shrink-0 flex items-center gap-2 pb-3 transition-all text-sm font-bold border-b-2 whitespace-nowrap ${activeCategory === cat.id ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-primary'}`}>
                            <cat.icon className="w-4 h-4" /> {cat.label}
                        </button>
                    ))}
                </div>

                {/* Quick Filters */}
                <div className="flex flex-nowrap md:flex-wrap items-center justify-between gap-4">
                    <div className="flex items-center gap-2 overflow-x-auto no-scrollbar pb-1 flex-grow">
                        {cities.map(city => (
                            <button key={city} onClick={() => setSelectedCity(city)} className={`flex-shrink-0 whitespace-nowrap px-4 py-2 rounded-full text-xs font-bold transition-all border ${selectedCity === city ? 'bg-primary text-white border-primary' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400 hover:bg-gray-50'}`}>{city}</button>
                        ))}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={() => setShowMap(!showMap)} className="flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-xs transition-all border bg-gray-900 text-white border-gray-900 hover:bg-black active:scale-95">
                            {showMap ? <><EyeOff className="w-3.5 h-3.5" /> Hide Map</> : <><MapIcon className="w-3.5 h-3.5" /> Show Map</>}
                        </button>
                        <button onClick={() => setShowMobileFilters(!showMobileFilters)} className={`flex-shrink-0 flex items-center gap-2 px-4 py-2 rounded-xl font-bold text-xs transition-all border active:scale-95 ${showMobileFilters ? 'bg-brand text-white border-brand' : 'bg-white border-gray-300 hover:border-black'}`}>
                            <Filter className="w-3.5 h-3.5" /> Filters {activeFiltersCount > 0 && `(${activeFiltersCount})`}
                        </button>
                    </div>
                </div>
            </div>

            {/* ADVANCED FILTERS PANEL */}
            {showMobileFilters && (
                <div className="mb-8 p-4 md:p-6 bg-gray-50 rounded-3xl border border-gray-200 animate-fade-in relative z-20">
                    <div className="flex justify-end"><button onClick={() => setShowMobileFilters(false)}><X className="w-5 h-5"/></button></div>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
                        {/* Property Type */}
                        <div className="lg:col-span-4">
                            <h4 className="font-bold text-primary mb-3 text-sm flex items-center gap-2"><Building className="w-4 h-4"/> Property Type</h4>
                            <div className="flex gap-2 flex-wrap">
                                {propertyTypes.map(type => (
                                    <button key={type} onClick={() => setSelectedType(type)} className={`px-4 py-2 rounded-lg text-xs font-bold border transition-all active:scale-95 ${selectedType === type ? 'bg-brand text-white border-brand' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>{type}</button>
                                ))}
                            </div>
                        </div>

                        {/* Rooms */}
                        <div className="lg:col-span-2 flex flex-col md:flex-row gap-4 md:gap-6">
                            <div className="flex-1">
                                <h4 className="font-bold text-primary mb-3 text-sm flex items-center gap-2"><Bed className="w-4 h-4"/> Bedrooms (Min)</h4>
                                <div className="flex items-center border border-gray-300 rounded-xl bg-white overflow-hidden h-12">
                                    <button onClick={() => setMinBedrooms(Math.max(0, minBedrooms - 1))} className="w-12 h-full hover:bg-gray-100 border-r border-gray-200 flex items-center justify-center active:bg-gray-200 text-lg">-</button>
                                    <div className="flex-1 text-center font-bold text-base">{minBedrooms > 0 ? `${minBedrooms}+` : 'Any'}</div>
                                    <button onClick={() => setMinBedrooms(minBedrooms + 1)} className="w-12 h-full hover:bg-gray-100 border-l border-gray-200 flex items-center justify-center active:bg-gray-200 text-lg">+</button>
                                </div>
                            </div>
                            <div className="flex-1">
                                <h4 className="font-bold text-primary mb-3 text-sm flex items-center gap-2"><Bath className="w-4 h-4"/> Bathrooms (Min)</h4>
                                <div className="flex items-center border border-gray-300 rounded-xl bg-white overflow-hidden h-12">
                                    <button onClick={() => setMinBaths(Math.max(0, minBaths - 1))} className="w-12 h-full hover:bg-gray-100 border-r border-gray-200 flex items-center justify-center active:bg-gray-200 text-lg">-</button>
                                    <div className="flex-1 text-center font-bold text-base">{minBaths > 0 ? `${minBaths}+` : 'Any'}</div>
                                    <button onClick={() => setMinBaths(minBaths + 1)} className="w-12 h-full hover:bg-gray-100 border-l border-gray-200 flex items-center justify-center active:bg-gray-200 text-lg">+</button>
                                </div>
                            </div>
                        </div>

                        {/* Price */}
                        <div className="lg:col-span-2">
                            <h4 className="font-bold text-primary mb-3 text-sm flex items-center gap-2"><DollarSign className="w-4 h-4"/> Price per night</h4>
                            <div className="flex items-center gap-3">
                                <input type="number" placeholder="Min" value={priceRange[0] || ''} onChange={(e) => setPriceRange([Number(e.target.value), priceRange[1]])} className="w-full p-3 rounded-xl border border-gray-300 text-sm focus:border-brand outline-none" />
                                <span className="text-gray-400 font-bold">-</span>
                                <input type="number" placeholder="Max" value={priceRange[1]} onChange={(e) => setPriceRange([priceRange[0], Number(e.target.value)])} className="w-full p-3 rounded-xl border border-gray-300 text-sm focus:border-brand outline-none" />
                            </div>
                        </div>

                        {/* Amenities */}
                        <div className="lg:col-span-4 border-t border-gray-100 pt-6 mt-2">
                            <h4 className="font-bold text-primary mb-3 text-sm flex items-center gap-2"><Check className="w-4 h-4"/> Amenities</h4>
                            <div className="flex flex-wrap gap-2">
                                {amenitiesMap.map(amenity => (
                                    <button key={amenity.key} onClick={() => toggleAmenity(amenity.key)} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-semibold transition-all border active:scale-95 ${selectedAmenities.includes(amenity.key) ? 'bg-brand/10 border-brand text-brand' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-400'}`}>
                                        {selectedAmenities.includes(amenity.key) && <Check className="w-3 h-3" />}
                                        {amenity.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
                        <button onClick={() => { setSelectedAmenities([]); setPriceRange([0, 50000]); setSelectedCity('All'); setSelectedType('All'); setMinBedrooms(0); setMinBaths(0); }} className="text-gray-500 underline text-xs hover:text-brand font-bold flex items-center gap-1 p-2">
                            <X className="w-3 h-3" /> Clear all
                        </button>
                    </div>
                </div>
            )}

            {/* --- MAPPA --- */}
            {showMap && !loading && (
                <div className="w-full h-[350px] md:h-[450px] rounded-3xl overflow-hidden shadow-lg border border-gray-200 relative animate-fade-in z-0 mb-10">
                    <style>{`.leaflet-tile-pane { filter: saturate(0.6) contrast(1.1) brightness(1.02); }`}</style>
                    <MapContainer center={mapCenter} zoom={12} scrollWheelZoom={false} className="w-full h-full">
                        <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions" target="_blank" rel="noreferrer">CARTO</a>' url={`https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=${process.env.REACT_APP_CARTO_API_KEY ?? ''}`} />

                        {/* Map Updater per ricentrare la mappa */}
                        <MapUpdater center={mapCenter} />

                        {/* Marker Risultati Esatti */}
                        {exactMatches.map((property) => {
                            // LOGICA: Mostra Totale solo se la ricerca è confermata (isSearchApplied) E esiste totalPrice
                            const showTotal = isSearchApplied && property.totalPrice;

                            const priceLabel = showTotal
                                ? `$${Math.round(property.totalPrice!).toLocaleString('en-US')}`
                                : `$${Number(property.price).toLocaleString('en-US')}`;

                            return (
                                property.lat && property.lng && (
                                    <Marker
                                        key={property.id}
                                        position={[property.lat, property.lng]}
                                        icon={createPriceIcon(priceLabel, false)}
                                    >
                                        <Popup className="custom-popup">
                                            <div className="w-52 p-1 font-sans">
                                                <div className="aspect-video w-full rounded-lg overflow-hidden mb-2">
                                                    <img src={property.image} alt={property.title} className="w-full h-full object-cover" onError={(e) => { (e.target as HTMLImageElement).src = 'https://via.placeholder.com/300x200?text=No+Image'; }} />
                                                </div>
                                                <h3 className="font-bold text-gray-900 text-sm mb-1">{property.title}</h3>
                                                <p className="text-brand font-bold text-xs">
                                                    {showTotal ? 'Total Price' : 'Per Night'} {showTotal
                                                    ? `$${Math.round(property.totalPrice!).toLocaleString()}`
                                                    : `$${property.price.toLocaleString()}`}
                                                </p>

                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigateToDetails(property.id);
                                                    }}
                                                    className="w-full bg-gray-900 text-white text-xs font-bold py-2 rounded-lg hover:bg-black transition-all flex items-center justify-center gap-1 shadow-sm active:scale-95"
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        </Popup>
                                    </Marker>
                                )
                            );
                        })}

                        {/* Marker Suggeriti */}
                        {suggestedMatches.map((property) => {
                            const showTotal = isSearchApplied && property.totalPrice;

                            const priceLabel = showTotal
                                ? `$${Math.round(property.totalPrice!).toLocaleString('en-US')}`
                                : `$${Number(property.price).toLocaleString('en-US')}`;

                            return (
                                property.lat && property.lng && (
                                    <Marker
                                        key={`sugg-${property.id}`}
                                        position={[property.lat, property.lng]}
                                        icon={createPriceIcon(priceLabel, true)}
                                    >
                                        <Popup className="custom-popup">
                                            <div className="w-52 p-1 font-sans">
                                                <div className="aspect-video w-full rounded-lg overflow-hidden mb-2">
                                                    <img src={property.image} alt={property.title} className="w-full h-full object-cover" />
                                                </div>
                                                <h3 className="font-bold text-gray-900 text-sm mb-1">{property.title}</h3>
                                                <p className="text-gray-500 font-bold text-xs flex items-center gap-1">
                                                    <MapPin className="w-3 h-3"/> {(property as any).distanceFromSearch ? `${(property as any).distanceFromSearch} Miles away` : 'Alternative'}
                                                </p>
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigateToDetails(property.id);
                                                    }}
                                                    className="w-full bg-gray-900 text-white text-xs font-bold py-2 rounded-lg hover:bg-black transition-all flex items-center justify-center gap-1 shadow-sm active:scale-95"
                                                >
                                                    View Details
                                                </button>
                                            </div>
                                        </Popup>
                                    </Marker>
                                )
                            );
                        })}
                    </MapContainer>
                </div>
            )}

            {/* --- LISTA RISULTATI --- */}
            <div className="pb-20">
                {loading ? (
                    <div className="flex justify-center items-center py-40"><Loader2 className="w-12 h-12 text-brand animate-spin" /></div>
                ) : (
                    <>
                        {/* 1. RISULTATI ESATTI */}
                        {exactMatches.length > 0 && (
                            <div className="mb-12">
                                <h3 className="text-xl font-serif font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    {searchParams.location && searchParams.location !== 'All' && searchParams.location !== '' ? `Stays in ${searchParams.location}` : 'Available Properties'}
                                    <span className="bg-gray-100 text-gray-600 text-xs px-2 py-1 rounded-full font-sans">{exactMatches.length} found</span>
                                </h3>
                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
                                    {exactMatches.map(p => (
                                        <PropertyCard
                                            key={p.id}
                                            p={p}
                                            checkIn={searchParams.checkIn}
                                            checkOut={searchParams.checkOut}
                                            guests={searchParams.guests}
                                            distanceLabel={null} // Nessuna distanza per match esatti
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* 2. SUGGERIMENTI */}
                        {suggestedMatches.length > 0 && (
                            <div className="animate-fade-in">
                                <div className="flex items-center gap-4 mb-8">
                                    <div className="h-px bg-gray-200 flex-grow"></div>
                                    <div className="text-center">
                                        <h3 className="text-lg font-serif font-bold text-gray-900">
                                            {exactMatches.length === 0 ? "No exact matches found" : "Also available for your dates"}
                                        </h3>
                                        <p className="text-gray-500 text-sm">
                                            {searchParams.location ? `Other properties available outside ${searchParams.location}` : "Other available properties"}
                                        </p>
                                    </div>
                                    <div className="h-px bg-gray-200 flex-grow"></div>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12">
                                    {suggestedMatches.map(p => (
                                        <PropertyCard
                                            key={p.id}
                                            p={p}
                                            checkIn={searchParams.checkIn}
                                            checkOut={searchParams.checkOut}
                                            guests={searchParams.guests}
                                            // PASSAGGIO DELLA DISTANZA CALCOLATA
                                            distanceLabel={(p as any).distanceFromSearch ? `${(p as any).distanceFromSearch} miles away` : null}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* NESSUN RISULTATO ASSOLUTO */}
                        {exactMatches.length === 0 && suggestedMatches.length === 0 && (
                            <div className="text-center py-20 bg-gray-50 rounded-2xl border-2 border-dashed border-gray-200 mx-4 md:mx-0">
                                <Filter className="w-10 h-10 text-gray-300 mx-auto mb-3"/>
                                <h3 className="text-lg font-bold text-gray-900">No properties available</h3>
                                <p className="text-gray-500 text-sm mt-1">Try changing your dates or clearing filters.</p>
                                <button onClick={() => { setSelectedCity('All'); setPriceRange([0, 50000]); setSelectedAmenities([]); setSelectedType('All'); }} className="mt-4 text-brand font-bold text-sm hover:underline">Clear Filters</button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default VillasContent;